/**
 * Navigation — CHI Alliance
 * Desktop: single clean row — linked logo mark, nav links centered, CTA right
 * Mobile: hamburger full-screen drawer
 * Obsidian Pulse cue: the menu trigger carries Chi magenta as the compact navigation signal.
 */
import { useState, useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, User, LogIn, UserPlus, Calendar, Settings, LogOut, Shield, Users, Mic2, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, type UserProfile } from "@/contexts/AuthContext";
import { personInitials } from "@/lib/personDisplay";
import { trpc } from "@/lib/trpc";
import CtaIcon from "@/components/CtaIcon";
import NotificationBell from "@/components/NotificationBell";

/** The gradient that travels around a hovered nav item. */
const RING_GRADIENT =
  "linear-gradient(115deg, rgba(0,212,255,0.95), rgba(168,85,247,0.9) 46%, rgba(233,30,140,0.95) 100%)";

/**
 * The active page's ring: the same three colours at full strength.
 *
 * The active item has to be identifiable at a glance without re-reading the
 * labels, so it keeps the ring permanently and is also larger and heavier than
 * its neighbours -- one signal is easy to miss on a dark header, three are not.
 */
const RING_GRADIENT_ACTIVE =
  "linear-gradient(115deg, #00D4FF, #A855F7 46%, #FF2D9B 100%)";

/**
 * The label's own gradient.
 *
 * Carried on an inner span rather than on the link: painting text with
 * `background-clip: text` needs the element's background for the glyphs, and the
 * ring needs it for the border, so one element cannot do both.
 */
/*
 * Bright, and routed through white at the midpoint.
 *
 * The first version ran cyan into a light purple into pink, and the purple
 * sat at roughly the luminance of the header behind it, so the middle of every
 * label went soft. Passing through white keeps the ramp obviously a gradient
 * while giving each word a legible core.
 */
const LABEL_GRADIENT = "linear-gradient(112deg, #86F7FF 0%, #FFFFFF 48%, #FFA3D8 100%)";

/** Applied to the label span when a link is active or hovered. */
const gradientLabelStyle: CSSProperties = {
  backgroundImage: LABEL_GRADIENT,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
};

/** Plain white, for every other state. */
const plainLabelStyle: CSSProperties = {
  backgroundImage: "none",
  WebkitTextFillColor: "#FFFFFF",
  color: "#FFFFFF",
};

function applyLabelStyle(link: HTMLElement, gradient: boolean) {
  const label = link.querySelector<HTMLElement>(".chi-nav-label");
  if (!label) return;
  Object.assign(label.style, gradient ? gradientLabelStyle : plainLabelStyle);
}

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/mission-and-values", label: "Mission & Values" }, // route: /mission-and-values
  { href: "/community", label: "Community" },
  { href: "/collaborate-with-us", label: "Partners" },
  { href: "/hr-services", label: "HR Services" },
  { href: "/calendar-of-events", label: "Events" },
];

interface AccountLink {
  icon: ReactNode;
  label: string;
  color: string;
  href: string;
}

/**
 * Height of the fixed navigation bar, in pixels.
 *
 * Named because two things depend on it agreeing: the bar's own height, and where
 * the account panel opens. The panel is positioned from its trigger, which is
 * centred in the bar, so it needs this number to land flush against the bar's
 * lower edge rather than a few pixels inside it.
 */
const NAV_BAR_HEIGHT = 72;

/** Breathing room between the bar's lower border and the account panel, in pixels. */
const ACCOUNT_PANEL_GAP = 5;

/** The account trigger's unhovered, unopened fill. Referenced by the hover handlers
 *  as well as the initial style, so a restored background cannot drift from it. */
const ACCOUNT_BUTTON_REST_BACKGROUND =
  "linear-gradient(135deg, rgba(255,255,255,0.12), rgba(223,109,168,0.09))";

/**
 * The signed-in account destinations, derived once and rendered by both the
 * desktop dropdown and the mobile drawer. Kept in one place deliberately: when
 * the two lists were written out twice, a phone could be shown a different set
 * of destinations — or a stale entitlement offer — than the same account saw on
 * a laptop.
 */
function accountLinksFor(user: UserProfile | null, hasSpeakerWorkspace: boolean): AccountLink[] {
  const speakerWorkspaceLink: AccountLink[] = hasSpeakerWorkspace
    ? [{ icon: <Mic2 size={14} />, label: "Speaker Workspace", color: "#F5A623", href: "/speaker-portal" }]
    : [];

  const baseLinks = [
    { icon: <Calendar size={14} />, label: "My Events", color: "#00D4FF", href: "/my-events" },
    ...speakerWorkspaceLink,
    { icon: <Users size={14} />, label: "Member Directory", color: "#E91E8C", href: "/member-directory" },
    { icon: <User size={14} />, label: "My Profile", color: "#A855F7", href: "/my-profile" },
    { icon: <Settings size={14} />, label: "Settings", color: "#64748B", href: "/my-profile" },
  ];

  /*
   * An administrator gets the CRM in addition to everything a member sees, not
   * instead of it. Admin accounts hold full site access (`hasFullSiteAccess`
   * returns true on role alone), so withholding the member destinations from the
   * one account entitled to all of them would be a menu contradicting the
   * permissions behind it.
   */
  if (user?.role === "admin") {
    return [
      { icon: <Shield size={14} />, label: "Admin CRM", color: "#A855F7", href: "/admin" },
      { icon: <Shield size={14} />, label: "Event check-in", color: "#D5B073", href: "/event-check-in" },
      ...baseLinks,
    ];
  }

  return baseLinks;
}

/**
 * Mobile account row. `minHeight: 48` is the point of it: these are the only
 * account controls a phone has, so they have to clear the 44px minimum tap
 * target rather than inherit the dense spacing the desktop dropdown can afford.
 */
function mobileAccountItemStyle(accent: string): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    width: "100%",
    minHeight: 48,
    padding: "0.6rem 0.75rem",
    borderRadius: "0.6rem",
    background: "linear-gradient(135deg, rgba(255,255,255,0.13), rgba(255,255,255,0.065))",
    border: `1px solid ${accent}40`,
    color: "white",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: "1rem",
    fontWeight: 600,
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16), 0 10px 24px rgba(35,4,38,0.12)",
    backdropFilter: "blur(14px) saturate(140%)",
    WebkitBackdropFilter: "blur(14px) saturate(140%)",
  };
}

function mobileAccountIconStyle(accent: string): CSSProperties {
  return {
    width: 30,
    height: 30,
    borderRadius: "0.4rem",
    background: `${accent}1F`,
    border: `1px solid ${accent}33`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: accent,
    flexShrink: 0,
  };
}

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const { isLoggedIn, user, logout } = useAuth();
  const [location, navigate] = useLocation();
  const speakerAccess = trpc.speakers.access.useQuery(undefined, { enabled: isLoggedIn });
  const loginRef = useRef<HTMLDivElement>(null);
  const accountLinks = accountLinksFor(user, speakerAccess.data?.state === "approved");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (loginRef.current && !loginRef.current.contains(e.target as Node)) {
        setLoginOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      {/* ── DESKTOP / TABLET NAV ── */}
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          height: `${NAV_BAR_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          isolation: "isolate",
          overflow: "visible",
          background: scrolled
            ? "linear-gradient(108deg, rgba(13,10,27,0.94) 0%, rgba(75,21,64,0.9) 48%, rgba(21,12,32,0.94) 100%)"
            : "linear-gradient(108deg, rgba(17,11,31,0.87) 0%, rgba(91,23,70,0.81) 48%, rgba(25,13,37,0.87) 100%)",
          backdropFilter: "blur(22px) saturate(128%)",
          WebkitBackdropFilter: "blur(22px) saturate(128%)",
          borderBottom: scrolled
            ? "1px solid rgba(255,255,255,0.2)"
            : "1px solid rgba(255,255,255,0.14)",
          boxShadow: scrolled
            ? "0 16px 38px rgba(2,2,12,0.42), 0 5px 16px rgba(190,49,125,0.16)"
            : "0 14px 34px rgba(2,2,12,0.33), 0 4px 14px rgba(157,40,103,0.14)",
          transition: "background 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease, backdrop-filter 0.28s ease",
        }}
      >
        {/* Gradient fade overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            background: "linear-gradient(to right, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.018) 44%, rgba(239,125,182,0.1) 72%, rgba(192,87,153,0.06) 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            height: "clamp(44px, 6vw, 76px)",
            background: scrolled
              ? "linear-gradient(to bottom, rgba(66,21,61,0.28) 0%, rgba(33,16,45,0.12) 42%, transparent 100%)"
              : "linear-gradient(to bottom, rgba(91,24,70,0.32) 0%, rgba(42,17,50,0.14) 42%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 0,
            transition: "background 0.28s ease",
          }}
        />
        <div
          style={{
            width: "100%",
            // On wide screens the navigation previously stayed inside a narrow 1280px
            // rail, leaving a large inactive area at either side of the menu. This
            // wider cap lets the five destinations occupy the header deliberately
            // while retaining generous outer gutters on very large displays.
            maxWidth: "min(96vw, 1600px)",
            margin: "0 auto",
            padding: "0 clamp(0.875rem, 3vw, 3.5rem)",
            display: "flex",
            alignItems: "center",
            gap: "clamp(0.75rem, 1.7vw, 2rem)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* ── LOGO MARK ── */}
          <Link
            href="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.625rem",
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <img
              src="/media/brand/chi-alliance-logo.png"
              alt="The Chi Alliance"
              style={{ height: "clamp(64.2px, 7.1vw, 81.1px)", width: "auto", objectFit: "contain", flexShrink: 0 }}
            />
          </Link>

          {/* ── NAV LINKS — centered, single row ── */}
          <nav
            className="hidden lg:flex"
            style={{
              // Space the destination group evenly inside the space that remains
              // between the brand and account controls, rather than clustering it
              // around the header midpoint.
              flex: "1 1 0",
              minWidth: 0,
              justifyContent: "space-evenly",
              alignItems: "center",
              gap: 0,
            }}
          >
            {navLinks.map((link) => {
              const isActive = location === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: isActive
                      ? "clamp(1rem, 1.18vw, 1.14rem)"
                      : "clamp(0.92rem, 1.08vw, 1.05rem)",
                    fontWeight: isActive ? 800 : 700,
                    letterSpacing: isActive ? "0.045em" : "0.03em",
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    // Colour lives on the label span below.
                    padding: "0.5rem clamp(0.55rem, 0.72vw, 0.85rem)",
                    borderRadius: "0.5rem",
                    color: "#FFFFFF",
                    backgroundColor: "transparent",
                    /*
                     * A gradient ring, not a raised tile.
                     *
                     * The interior padding-box layer is fully transparent, so the
                     * only thing drawn is the border-box gradient travelling around
                     * the label and the header shows straight through it. The
                     * elevation this used to carry -- a drop shadow plus an inset
                     * top highlight -- is gone: the item now reads as outlined
                     * rather than lifted, and scale alone carries the interaction.
                     */
                    backgroundImage: isActive
                      ? `linear-gradient(transparent, transparent) padding-box, ${RING_GRADIENT_ACTIVE} border-box`
                      : "none",
                    backgroundClip: "padding-box, border-box",
                    backgroundOrigin: "padding-box, border-box",
                    backgroundRepeat: "no-repeat",
                    border: "1px solid transparent",
                    boxShadow: "none",
                    // Neon read comes from a glow on the mark itself, not from a
                    // raised surface behind it.
                    // Stacked shadows on the glyphs themselves, so the active
                    // page reads as lit rather than as a shadowed box.
                    filter: isActive
                      ? "drop-shadow(0 0 6px rgba(255,111,192,0.75)) drop-shadow(0 0 14px rgba(168,85,247,0.6)) drop-shadow(0 0 26px rgba(0,212,255,0.4))"
                      : "none",
                    transform: isActive ? "scale(1.1)" : "scale(1)",
                    transformOrigin: "center",
                    transition: "color 0.18s ease, background-image 0.22s ease, transform 0.28s cubic-bezier(0.23, 1, 0.32, 1), filter 0.24s ease",
                    textDecoration: "none",
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.color = "#FFFFFF";
                    // The ring appears and the label grows; nothing lifts.
                    // The active item is already ringed and larger; hovering it
                    // must not shrink it back to the hover size.
                    if (!isActive) {
                      el.style.backgroundImage = `linear-gradient(transparent, transparent) padding-box, ${RING_GRADIENT} border-box`;
                      el.style.transform = "scale(1.06)";
                      el.style.filter = "drop-shadow(0 0 8px rgba(255,111,192,0.45))";
                    }
                    applyLabelStyle(el, true);
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.color = "#FFFFFF";
                    el.style.backgroundImage = isActive
                      ? `linear-gradient(transparent, transparent) padding-box, ${RING_GRADIENT_ACTIVE} border-box`
                      : "none";
                    el.style.transform = isActive ? "scale(1.1)" : "scale(1)";
                    if (!isActive) el.style.filter = "none";
                    applyLabelStyle(el, isActive);
                  }}
                >
                  <span
                    className="chi-nav-label"
                    style={isActive ? gradientLabelStyle : plainLabelStyle}
                  >
                    {link.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* ── RIGHT: CTA + mobile toggle ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexShrink: 0,
              marginLeft: "auto",
            }}
          >
            {/* CTA — desktop only, and only for visitors who have not signed in. */}
            {!isLoggedIn && (
              <Link
                href="/membership"
                className="hidden lg:inline-flex chi-btn-primary"
                style={{
                  minHeight: 36,
                  padding: "0.42rem 0.82rem",
                  fontSize: "0.78rem",
                  fontWeight: 900,
                  letterSpacing: "0.045em",
                  background: "linear-gradient(135deg, #FFFFFF 0%, #FCE3F2 100%)",
                  color: "#9D075D",
                  border: "1px solid rgba(255,255,255,0.94)",
                  boxShadow: "0 10px 24px rgba(58,2,42,0.34), inset 0 1px 0 rgba(255,255,255,0.95)",
                  whiteSpace: "nowrap",
                }}
              >
                Join free <CtaIcon icon={UserPlus} size={14} />
              </Link>
            )}

            {isLoggedIn && <NotificationBell />}

            {/* Login icon + dropdown — desktop only */}
            <div
              ref={loginRef}
              style={{
                position: "relative",
                zIndex: 101,
              }}
              className="hidden lg:block"
            >
              <button
                onClick={() => setLoginOpen(!loginOpen)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  // Signed out, the control carries a "Sign In" label. Signed in,
                  // it carries the member's own initials, name and address, so the
                  // account you are acting as is legible without opening the menu
                  // — a generic silhouette cannot tell you which account you are in.
                  gap: isLoggedIn ? "0.65rem" : "0.375rem",
                  width: isLoggedIn ? 260 : "auto",
                  height: isLoggedIn ? 48 : 40,
                  padding: isLoggedIn ? "0" : "0 0.75rem",
                  borderRadius: isLoggedIn ? 0 : "0.5rem",
                  // The signed-in identity belongs directly in the header. It is
                  // intentionally not wrapped in a pill, card, border, or glass
                  // treatment — only the menu itself receives a surface.
                  /*
                    Three distinct states, not two. Hover is a neutral brightening —
                    it says "this is clickable". Open is unmistakably the brand
                    magenta, because it has a different job: telling you which control
                    the panel below belongs to. A hover tint reused as the open state
                    leaves the button looking merely pointed-at while a menu is up.
                  */
                  background: isLoggedIn
                    ? loginOpen ? "rgba(233,30,140,0.16)" : "transparent"
                    : loginOpen
                      ? "linear-gradient(135deg, rgba(233,30,140,0.52), rgba(194,24,91,0.4))"
                      : ACCOUNT_BUTTON_REST_BACKGROUND,
                  border: isLoggedIn
                    ? "none"
                    : loginOpen ? "1px solid rgba(249,168,212,0.66)" : "1px solid rgba(248,184,218,0.25)",
                  color: "#FFF",
                  cursor: "pointer",
                  justifyContent: isLoggedIn ? "flex-start" : "center",
                  transition: "color 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.16s cubic-bezier(0.23, 1, 0.32, 1)",
                  position: "relative",
                  boxShadow: isLoggedIn
                    ? "none"
                    : loginOpen
                      ? "inset 0 1px 0 rgba(255,255,255,0.24), 0 0 0 3px rgba(233,30,140,0.18), 0 10px 26px rgba(233,30,140,0.28)"
                      : "inset 0 1px 0 rgba(255,255,255,0.19), 0 10px 24px rgba(2,4,14,0.25)",
                  backdropFilter: isLoggedIn ? "none" : "blur(14px) saturate(145%)",
                  WebkitBackdropFilter: isLoggedIn ? "none" : "blur(14px) saturate(145%)",
                }}
                /*
                  The hover handlers write the background inline, so they have to know
                  to leave the open state alone — otherwise moving the pointer over an
                  already-open button would repaint it as merely hovered.
                */
                onMouseEnter={e => {
                  if (!isLoggedIn && !loginOpen) {
                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(238,145,191,0.14))";
                  }
                }}
                onMouseLeave={e => {
                  if (!isLoggedIn && !loginOpen) {
                    e.currentTarget.style.background = ACCOUNT_BUTTON_REST_BACKGROUND;
                  }
                }}
                aria-haspopup="menu"
                aria-expanded={loginOpen}
                aria-label={isLoggedIn ? `Account menu for ${user?.name ?? "your account"}` : "Sign in to your account"}
              >
                {isLoggedIn ? (
                  <>
                    {/* Initials, not a stored avatar string, so the mark always
                        agrees with the name rendered beside it. */}
                    <span aria-hidden="true" style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #E91E8C, #F5A623)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "0.88rem",
                      fontWeight: 800,
                      letterSpacing: "0.02em",
                      color: "#FFF",
                    }}>
                      {personInitials(user?.name ?? "", "M")}
                    </span>
                    <span style={{ display: "grid", gap: "0.08rem", minWidth: 0, textAlign: "left" }}>
                      <span style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        minWidth: 0,
                        maxWidth: 210,
                      }}>
                        <span style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: "0.94rem",
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: "#FFF",
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                          {user?.name ?? "Member"}
                        </span>
                        <ChevronDown
                          size={15}
                          strokeWidth={2.5}
                          aria-hidden="true"
                          style={{
                            flexShrink: 0,
                            color: "rgba(255,240,250,0.86)",
                            transition: "transform 0.18s ease",
                            transform: loginOpen ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      </span>
                      {/* Truncated rather than wrapped: a long address must not
                          change the height of the navigation bar. */}
                      <span style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "0.73rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: "rgba(255,240,250,0.78)",
                        maxWidth: 210,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {user?.email ?? ""}
                      </span>
                    </span>
                  </>
                ) : (
                  <>
                    <User size={17} />
                    <span style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                      whiteSpace: "nowrap",
                    }}>
                      Sign In
                    </span>
                  </>
                )}
              </button>

              <AnimatePresence>
                {loginOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                    style={{
                      position: "absolute",
                      /*
                        Anchored to the navigation bar's lower edge, not the button's.
                        The trigger is centred inside a 72px bar, so measuring from the
                        button left the panel starting a few pixels short and sitting
                        over the bar it belongs under. Half the leftover height is
                        exactly the distance from the button's base to the bar's base;
                        the extra 5px is the breathing room that keeps the panel from
                        looking welded to the bar's border.
                      */
                      top: `calc(100% + ${(NAV_BAR_HEIGHT - (isLoggedIn ? 48 : 40)) / 2 + ACCOUNT_PANEL_GAP}px)`,
                      right: 0,
                      width: isLoggedIn ? 260 : 240,
                      background: "linear-gradient(150deg, rgba(19,13,34,0.93) 0%, rgba(86,24,69,0.9) 48%, rgba(25,15,42,0.94) 100%)",
                      backdropFilter: "blur(30px) saturate(138%)",
                      WebkitBackdropFilter: "blur(30px) saturate(138%)",
                      // The authenticated panel drops out from the identity's lower
                      // edge. Its open top remains deliberately square and borderless;
                      // only the exposed sides and base are framed in glass.
                      border: isLoggedIn ? "none" : "1px solid rgba(255,255,255,0.2)",
                      borderTop: "none",
                      borderRight: isLoggedIn ? "1px solid rgba(255,255,255,0.2)" : undefined,
                      borderBottom: isLoggedIn ? "1px solid rgba(255,255,255,0.2)" : undefined,
                      borderLeft: isLoggedIn ? "1px solid rgba(255,255,255,0.2)" : undefined,
                      borderRadius: isLoggedIn ? "0 0 0.875rem 0.875rem" : "0.875rem",
                      boxShadow: isLoggedIn
                        ? "0 24px 54px rgba(1,3,12,0.42), 0 0 0 1px rgba(206,115,205,0.08)"
                        : "0 24px 54px rgba(1,3,12,0.42), inset 0 1px 0 rgba(255,255,255,0.18), 0 0 0 1px rgba(206,115,205,0.08)",
                      overflow: "hidden",
                      transformOrigin: "top right",
                      zIndex: 100,
                    }}
                  >
                    {!isLoggedIn ? (
                      <>
                        {/* Header */}
                        <div style={{ padding: "1rem 1.25rem 0.75rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", color: "rgba(255,236,249,0.72)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Member Access</p>
                          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.98rem", fontWeight: 750, color: "white", letterSpacing: "-0.01em" }}>The Chi Alliance</p>
                        </div>
                        {/* Actions */}
                        <div style={{ padding: "0.5rem" }}>
                          <button
                            onClick={() => { setLoginOpen(false); navigate("/login"); }}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              padding: "0.75rem 0.875rem",
                              borderRadius: "0.5rem",
                              background: "transparent",
                              border: "none",
                              color: "white",
                              fontFamily: "'Space Grotesk', sans-serif",
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "background 0.15s ease",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <span style={{ width: 30, height: 30, borderRadius: "0.4rem", background: "rgba(233,30,140,0.15)", border: "1px solid rgba(233,30,140,0.25)", display: "flex", alignItems: "center", justifyContent: "center", color: "#E91E8C", flexShrink: 0 }}>
                              <LogIn size={14} />
                            </span>
                            Sign In
                          </button>
                          <button
                            onClick={() => { setLoginOpen(false); navigate("/create-account"); }}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              padding: "0.75rem 0.875rem",
                              borderRadius: "0.5rem",
                              background: "transparent",
                              border: "none",
                              color: "white",
                              fontFamily: "'Space Grotesk', sans-serif",
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "background 0.15s ease",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <span style={{ width: 30, height: 30, borderRadius: "0.4rem", background: "rgba(245,166,35,0.12)", border: "1px solid rgba(245,166,35,0.22)", display: "flex", alignItems: "center", justifyContent: "center", color: "#F5A623", flexShrink: 0 }}>
                              <UserPlus size={14} />
                            </span>
                            Create Account
                          </button>
                        </div>
                        <div style={{ padding: "0.75rem 1.25rem", borderTop: "1px solid rgba(255,255,255,0.12)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", color: "rgba(255,236,249,0.7)", textAlign: "center" }}>
                          Members get early access to events & speakers
                        </div>
                      </>
                    ) : (
                      <>
                        {/* The header identity is deliberately separate and
                            borderless; this menu is a standard, comfortably padded
                            action container instead of a physically merged shape. */}
                        <div style={{ padding: "0.625rem" }}>
                          {accountLinks.map(({ icon, label, color, href }) => (
                            <Link
                              key={label}
                              href={href}
                              onClick={() => setLoginOpen(false)}
                              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.13)"; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                              style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                padding: "0.65rem 0.875rem",
                                borderRadius: "0.5rem",
                                background: "transparent",
                                color: "white",
                                fontFamily: "'Space Grotesk', sans-serif",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                cursor: "pointer",
                                textDecoration: "none",
                                transition: "background 0.15s ease",
                              }}
                            >
                              <span style={{ width: 28, height: 28, borderRadius: "0.375rem", background: `${color}15`, border: `1px solid ${color}25`, display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
                                {icon}
                              </span>
                              {label}
                            </Link>
                          ))}
                          <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "0.45rem 0" }} />
                          <button
                            onClick={async () => {
                              try {
                                await logout();
                              } finally {
                                setLoginOpen(false);
                                navigate("/login");
                              }
                            }}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              padding: "0.65rem 0.875rem",
                              borderRadius: "0.5rem",
                              background: "transparent",
                              border: "none",
                              color: "#EF4444",
                              fontFamily: "'Space Grotesk', sans-serif",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "background 0.15s ease",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <span style={{ width: 28, height: 28, borderRadius: "0.375rem", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <LogOut size={14} />
                            </span>
                            Sign Out
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Compact account access is available before opening the full mobile drawer. */}
            <Link
              href={isLoggedIn ? "/my-profile" : "/login"}
              className="flex lg:hidden"
              onClick={() => setMobileOpen(false)}
              aria-label={isLoggedIn ? `Open profile for ${user?.name ?? "your account"}` : "Sign in to your account"}
              style={{
                alignItems: "center",
                justifyContent: "center",
                width: 46,
                height: 46,
                borderRadius: "0.8rem",
                background: "linear-gradient(145deg, rgba(255,255,255,0.13), rgba(205,135,221,0.08))",
                border: "1px solid rgba(255,255,255,0.24)",
                color: "#FFFFFF",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 10px 24px rgba(2,4,14,0.24)",
                backdropFilter: "blur(14px) saturate(145%)",
                WebkitBackdropFilter: "blur(14px) saturate(145%)",
                textDecoration: "none",
                flexShrink: 0,
              }}
            >
              {isLoggedIn ? (
                <span aria-hidden="true" style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #F5A623, #E91E8C)",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "0.76rem",
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  color: "#FFFFFF",
                }}>
                  {personInitials(user?.name ?? "", "M")}
                </span>
              ) : (
                <User size={18} aria-hidden="true" />
              )}
            </Link>

            {/* Mobile menu button — visible on mobile, hidden from the lg breakpoint up.
                Display is left to the utility classes: an inline display value outranks
                them and would keep the trigger on desktop. */}
            <button
              className="flex lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-controls="chi-mobile-menu"
              aria-expanded={mobileOpen}
              style={{
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "0.85rem",
                background: mobileOpen ? "linear-gradient(135deg, #D8277C, #A43D93)" : "linear-gradient(145deg, rgba(255,255,255,0.14), rgba(223,109,168,0.1))",
                border: "1px solid rgba(255,255,255,0.22)",
                outline: "none",
                color: "#FFFFFF",
                boxShadow: mobileOpen ? "0 12px 28px rgba(105,15,78,0.4), 0 0 22px rgba(216,39,124,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.2), 0 12px 26px rgba(2,4,14,0.26)",
                cursor: "pointer",
                flexShrink: 0,
                backdropFilter: "blur(14px) saturate(145%)",
                WebkitBackdropFilter: "blur(14px) saturate(145%)",
                transition: "background-color 0.18s cubic-bezier(0.23, 1, 0.32, 1), color 0.18s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.18s cubic-bezier(0.23, 1, 0.32, 1), transform 0.16s cubic-bezier(0.23, 1, 0.32, 1)",
              }}
              onMouseEnter={event => {
                event.currentTarget.style.background = "linear-gradient(135deg, #D8277C, #A43D93)";
                event.currentTarget.style.color = "#FFFFFF";
                event.currentTarget.style.boxShadow = "0 14px 30px rgba(105,15,78,0.5), 0 0 26px rgba(216,39,124,0.24)";
              }}
              onMouseLeave={event => {
                event.currentTarget.style.background = mobileOpen ? "linear-gradient(135deg, #D8277C, #A43D93)" : "linear-gradient(145deg, rgba(255,255,255,0.14), rgba(223,109,168,0.1))";
                event.currentTarget.style.color = "#FFFFFF";
                event.currentTarget.style.boxShadow = mobileOpen ? "0 12px 28px rgba(105,15,78,0.4), 0 0 22px rgba(216,39,124,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.2), 0 12px 26px rgba(2,4,14,0.26)";
              }}
              onFocus={event => {
                event.currentTarget.style.background = "linear-gradient(135deg, #D8277C, #A43D93)";
                event.currentTarget.style.color = "#FFFFFF";
                event.currentTarget.style.boxShadow = "0 0 0 3px rgba(125,234,255,0.42), 0 14px 30px rgba(105,15,78,0.5)";
              }}
              onBlur={event => {
                event.currentTarget.style.background = mobileOpen ? "linear-gradient(135deg, #D8277C, #A43D93)" : "linear-gradient(145deg, rgba(255,255,255,0.14), rgba(223,109,168,0.1))";
                event.currentTarget.style.color = "#FFFFFF";
                event.currentTarget.style.boxShadow = mobileOpen ? "0 12px 28px rgba(105,15,78,0.4), 0 0 22px rgba(216,39,124,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.2), 0 12px 26px rgba(2,4,14,0.26)";
              }}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} strokeWidth={2.5} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── MOBILE FULL-SCREEN DRAWER ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="chi-mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              background: "linear-gradient(160deg, rgba(15,11,30,0.97) 0%, rgba(81,23,65,0.95) 52%, rgba(20,13,37,0.98) 100%)",
              backdropFilter: "blur(28px) saturate(132%)",
              WebkitBackdropFilter: "blur(28px) saturate(132%)",
              display: "flex",
              flexDirection: "column",
              // The account section makes the drawer taller than a short
              // viewport, so it scrolls rather than clipping Sign Out off the
              // bottom edge. Body scroll stays locked behind it.
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              paddingTop: "88px",
              paddingLeft: "1.5rem",
              paddingRight: "1.5rem",
              paddingBottom: "2rem",
            }}
          >
            {/* Nav links */}
            <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {navLinks.map((link, i) => {
                const isActive = location === link.href;
                return (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.055, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
                  >
                    <Link
                      href={link.href}
                      style={{
                        display: "block",
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "clamp(1.05rem, 5vw, 1.35rem)",
                        fontWeight: 700,
                        color: "white",
                        padding: "0.875rem 0.8rem",
                        border: "1px solid rgba(255,255,255,0.13)",
                        borderRadius: "0.6rem",
                        background: isActive ? "linear-gradient(100deg, rgba(255,255,255,0.22), rgba(255,255,255,0.1))" : "rgba(255,255,255,0.075)",
                        boxShadow: isActive ? "inset 0 1px 0 rgba(255,255,255,0.22), 0 10px 22px rgba(36,4,38,0.14)" : "inset 0 1px 0 rgba(255,255,255,0.08)",
                        textDecoration: "none",
                        letterSpacing: "-0.015em",
                      }}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                );
              })}
            </nav>

            {/*
              ── ACCOUNT SECTION ──
              The desktop account dropdown is `hidden lg:block`, so without this
              a phone had no route to sign in, reach a member destination, or
              sign out. Same destinations, same entitlement rules, driven by the
              same `accountLinksFor` list the dropdown uses.

              Every item closes the drawer explicitly. The drawer also closes on
              location change, but that alone leaves a full-screen overlay
              covering the page when the tapped destination is the current route.
            */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
              style={{
                marginTop: "1.5rem",
                paddingTop: "1.25rem",
                borderTop: "1px solid rgba(233,30,140,0.28)",
              }}
            >
              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#E91E8C",
                  marginBottom: "0.5rem",
                }}
              >
                {isLoggedIn ? "Your Account" : "Member Access"}
              </p>

              {isLoggedIn && (
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "0.8rem",
                    color: "#94A3B8",
                    marginBottom: "0.75rem",
                    overflowWrap: "anywhere",
                  }}
                >
                  {user?.name ?? "Member"}
                  {user?.email ? ` · ${user.email}` : ""}
                </p>
              )}

              {/*
                Landmark-labelled rather than per-item aria-labels: each control's
                visible text is already its accessible name, and adding a second
                wording here would have collided with the desktop trigger's
                "Sign in to your account" label under assistive tech.
              */}
              <nav
                aria-label="Account"
                style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}
              >
                {!isLoggedIn ? (
                  <>
                    <button
                      type="button"
                      onClick={() => { setMobileOpen(false); navigate("/login"); }}
                      style={mobileAccountItemStyle("#E91E8C")}
                    >
                      <span style={mobileAccountIconStyle("#E91E8C")} aria-hidden="true">
                        <LogIn size={15} />
                      </span>
                      Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMobileOpen(false); navigate("/create-account"); }}
                      style={mobileAccountItemStyle("#F5A623")}
                    >
                      <span style={mobileAccountIconStyle("#F5A623")} aria-hidden="true">
                        <UserPlus size={15} />
                      </span>
                      Create Account
                    </button>
                  </>
                ) : (
                  <>
                    {accountLinks.map(({ icon, label, color, href }) => (
                      <Link
                        key={label}
                        href={href}
                        onClick={() => setMobileOpen(false)}
                        style={{ ...mobileAccountItemStyle(color), textDecoration: "none" }}
                      >
                        <span style={mobileAccountIconStyle(color)} aria-hidden="true">{icon}</span>
                        {label}
                      </Link>
                    ))}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await logout();
                        } finally {
                          setMobileOpen(false);
                          navigate("/login");
                        }
                      }}
                      style={{ ...mobileAccountItemStyle("#EF4444"), color: "#EF4444" }}
                    >
                      <span style={mobileAccountIconStyle("#EF4444")} aria-hidden="true">
                        <LogOut size={15} />
                      </span>
                      Sign Out
                    </button>
                  </>
                )}
              </nav>
            </motion.div>

            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              style={{ display: "flex", flexDirection: "column", gap: "0.875rem", marginTop: "2rem" }}
            >
              {/* The drawer closes itself on location change, so no handler here. */}
              {!isLoggedIn && (
                <Link
                  href="/membership"
                  className="chi-btn-primary"
                  style={{ textAlign: "center", fontSize: "0.82rem", fontWeight: 900, boxShadow: "0 14px 30px rgba(233,30,140,0.32), inset 0 1px 0 rgba(255,255,255,0.22)" }}
                >
                  Join free <CtaIcon icon={UserPlus} size={15} />
                </Link>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "1.5rem",
                  color: "#64748B",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "0.8rem",
                }}
              >
                <a href="tel:+12065375222" style={{ color: "#64748B", textDecoration: "none" }}>
                  206-537-5222
                </a>
                <span>·</span>
                <a href="mailto:info@thechialliance.com" style={{ color: "#64748B", textDecoration: "none" }}>
                  info@thechialliance.com
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
